
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import WebsiteLogo from "@/components/common/WebsiteLogo";
import CampManagedBanner from "@/components/common/CampManagedBanner";
import WelcomeController from "@/components/welcome/WelcomeController";
import WelcomeFooter from "@/components/welcome/components/WelcomeFooter";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEmailVerifiedToast } from "@/hooks/useEmailVerifiedToast";
import { TokenErrorBoundary } from "@/components/TokenErrorBoundary";

const WelcomePage: React.FC = () => {
  const { loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenValue = searchParams.get('token');
  const successParam = searchParams.get('success');
  const loadingParam = searchParams.get('loading');
  const [showSuccessBanner, setShowSuccessBanner] = useState(successParam === 'account-created');
  const [campManagedWorkflowStatus, setCampManagedWorkflowStatus] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(loadingParam === 'auth');
  
  // Handle email verification success toast
  useEmailVerifiedToast();
  
  console.log("WelcomePage: Rendering with token:", tokenValue);

  // Listen for workflow status updates from the editing state to render top-of-page banner
  useEffect(() => {
    const handler = (e: any) => {
      const status = e?.detail?.workflowStatus ?? null;
      setCampManagedWorkflowStatus(status);
    };
    window.addEventListener("campManagedWorkflowStatusUpdated", handler);
    return () => window.removeEventListener("campManagedWorkflowStatusUpdated", handler);
  }, []);

  // Handle banner dismissal
  const handleDismissBanner = () => {
    setShowSuccessBanner(false);
    // Remove success parameter from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('success');
    setSearchParams(newSearchParams);
  };

  // Clear auth loading after a delay to allow auth state to settle
  useEffect(() => {
    if (authLoading) {
      const timer = setTimeout(() => {
        setAuthLoading(false);
        // Clean up account creation flags
        localStorage.removeItem('kb_account_creation_flow');
        // Also remove loading parameter from URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('loading');
        setSearchParams(newSearchParams);
      }, 3000); // 3 second delay to allow auth to settle
      
      return () => clearTimeout(timer);
    }
  }, [authLoading, searchParams, setSearchParams]);

  // Show loading state to prevent error flash during auth state determination
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <WebsiteLogo width={240} className="mb-8" />
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kidsbooked-blue mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? "Setting up your account..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <TokenErrorBoundary 
      fallbackMessage="There was an issue with your session. This sometimes happens when the page has been open for a long time."
      autoRetryAttempts={3}
    >
      <div className="min-h-screen bg-background flex flex-col items-center p-0">
        {/* Full-bleed top masthead banner for camp-managed statuses */}
        <CampManagedBanner workflowStatus={campManagedWorkflowStatus || undefined} heightPx={40} borderBottomOnly={true} />
        {/* Success Banner */}
        {showSuccessBanner && (
          <div className="w-full bg-green-50 border-b border-green-200 px-4 py-3">
            <div className="max-w-screen-xl mx-auto flex items-center justify-between">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="text-green-800 font-medium">
                  Account successfully created!
                </div>
                <div className="text-green-800 font-small sm:ml-4 mt-1 sm:mt-0">
                  You can also login to <a href="https://kidsbooked.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-900">kidsbooked.com</a> with this account.
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismissBanner}
                className="text-green-600 hover:text-green-800 hover:bg-green-100"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
        )}
        
        <div className="mt-8 mb-6">
          <WebsiteLogo width={180} />
        </div>
        <div className="text-center mb-4">
          
        </div>
        
        <div className="w-full mb-8 px-0 flex-grow">
          <WelcomeController tokenValue={tokenValue} />
        </div>
        
        <WelcomeFooter />
      </div>
    </TokenErrorBoundary>
  );
};
export default WelcomePage;
